/**
 * Reality-check data, keyed by catalog id.
 *
 * WHY THIS FILE EXISTS
 * The card photos sell the fantasy. This is the counterweight: altitude,
 * how much walking is actually involved, whether you ascend by cable car or
 * on foot, age suitability, and the season windows that make or break a stop.
 *
 * WHAT THIS IS NOT
 * These are not traveller reviews, scraped or otherwise. They are editorial
 * notes based on verifiable attributes of each site. Where a fact is not
 * known with confidence it is omitted — a missing field is honest, an
 * invented one could send someone to 5,200m with a toddler.
 *
 * VERIFICATION STATUS
 * Spot-checked against published sources on 2026-09-07: Potala (altitude,
 * ~1,000 steps, 1-hour limit, ~2,300 daily tickets), Yamdrok (lake 4,441m vs
 * Kamba La viewpoint ~4,770m), Jade Dragon (cable car 4,506m, boardwalk to
 * ~4,680m), Everest north base camp (5,200m), Zhangjiajie (Bailong elevator
 * replaces a 2-3 hour climb in ~80 seconds). Two real errors were found and
 * fixed: the Potala step count was understated as 300, and Yamdrok used the
 * lake surface rather than the pass people actually stand on.
 *
 * The rest is unverified editorial judgement. Anything that affects the
 * altitude guardrails should be checked before it is trusted.
 *
 * Hand-maintained. Unlike catalog.data.ts, this is not generated.
 */
import type { Guide } from "./types.js";

export const GUIDES: Record<string, Guide> = {
  // ---------------------------------------------------------------- 北京
  "greatwall-mutianyu": {
    effort: 3,
    access: "cable",
    realityEn:
      "Cable car up and toboggan down means you can skip almost all the climbing — but the wall itself is steep, uneven and has no handrails in places.",
    realityCn: "缆车上、滑道下，可以几乎不爬山。但长城本身坡陡、台阶高低不一，部分地段没有扶手。",
    tipsEn: [
      "Mutianyu is far less crowded than Badaling",
      "Allow 3-4 hours including transit from central Beijing",
    ],
    tipsCn: ["慕田峪比八达岭人少很多", "从北京市区往返加游览，预留 3-4 小时"],
    seasonEn: "Avoid midsummer heat and the first week of October.",
    seasonCn: "避开盛夏高温和十一黄金周。",
  },
  "forbidden-city": {
    effort: 3,
    access: "flat",
    realityEn:
      "It is far bigger than photos suggest — a straight walk through is over a kilometre, almost entirely without shade or seating.",
    realityCn: "比照片大得多，中轴线走完超过一公里，几乎全程没有遮阴和座位。",
    tipsEn: [
      "Real-name booking required, sells out in peak season",
      "Closed Mondays",
      "Enter at Meridian Gate, exit north — no re-entry",
    ],
    tipsCn: ["需实名预约，旺季常提前售罄", "周一闭馆", "午门进、神武门出，不可回头"],
    seasonEn: "Book several days ahead in summer and October.",
    seasonCn: "暑期和十月需提前几天预约。",
  },
  "temple-of-heaven": {
    effort: 2,
    access: "walk",
    tipsEn: [
      "Go early — locals fill the corridors with music and dance by 7am, which is the real draw",
    ],
    tipsCn: ["清早去 —— 七点左右长廊里全是唱歌跳舞的本地人，这才是精华"],
  },
  "hutong-crawl": {
    effort: 2,
    access: "walk",
    tipsEn: ["Cash still helps at the smallest stalls"],
    tipsCn: ["最小的摊位备点现金更保险"],
  },
  "798-art": {
    effort: 2,
    access: "walk",
    realityEn:
      "Many galleries are commercial and free, but the headline exhibitions are ticketed separately.",
    realityCn: "多数画廊免费且偏商业，重头展览要单独买票。",
  },
  "jingshan-sunset": {
    effort: 2,
    access: "stairs",
    realityEn: "The classic view over the Forbidden City requires a short but steep stair climb.",
    realityCn: "俯瞰紫禁城的机位要爬一段短而陡的台阶。",
    tipsEn: ["The summit pavilion gets shoulder-to-shoulder at sunset"],
    tipsCn: ["日落时山顶亭子人挤人"],
  },
  "national-museum": {
    effort: 2,
    access: "flat",
    tipsEn: ["Free but requires advance booking with passport"],
    tipsCn: ["免费，但需凭护照提前预约"],
  },

  // ---------------------------------------------------------------- 西安
  terracotta: {
    effort: 2,
    access: "flat",
    realityEn:
      "You view the pits from raised walkways at a distance — you cannot get close to individual warriors. Pit 1 is the famous one; pits 2 and 3 are much smaller.",
    realityCn: "全程在高处围栏观看，无法近距离接触兵马俑。一号坑是照片里那个，二、三号坑小很多。",
    tipsEn: ["It is an hour outside Xi'an — half a day minimum", "Crowds are heaviest 10am-2pm"],
    tipsCn: ["在西安城外一小时车程，至少半天", "上午十点到下午两点最挤"],
  },
  "muslim-quarter": {
    effort: 2,
    access: "walk",
    realityEn:
      "Heavily touristed and increasingly aimed at visitors rather than locals; the side lanes are better than the main drag.",
    realityCn: "游客化严重，主街偏向外地人，往两侧小巷走反而更好。",
  },
  "xian-citywall": {
    effort: 2,
    access: "walk",
    realityEn: "The full loop is 14km — most people rent a bike rather than walk it.",
    realityCn: "环城一圈 14 公里，多数人租自行车而不是步行。",
    tipsEn: ["Bike rental is time-limited, usually 2 hours"],
    tipsCn: ["租车有时限，通常 2 小时"],
  },

  // ---------------------------------------------------------------- 华阴
  huashan: {
    altitude: 2154,
    effort: 5,
    access: "cable",
    minAge: 12,
    realityEn:
      "The plank walk is a genuine cliff traverse on bolted boards with a harness, and it is a paid add-on with long queues, not part of the main route. Cable cars reach the peaks, so you can visit without the climb — but the plank walk itself is not for anyone uneasy with heights.",
    realityCn:
      "长空栈道是真正的悬崖木板路，要另外付费租安全带，排队很久，不含在普通门票路线里。索道可直达峰顶，不爬也能上山 —— 但栈道本身恐高者绝对不要尝试。",
    tipsEn: [
      "Cable car up and down is the normal choice; the full hike is 6+ hours",
      "Not suitable for young children",
    ],
    tipsCn: ["常规做法是索道上下；全程徒步 6 小时以上", "不适合幼童"],
  },

  // ---------------------------------------------------------------- 成都
  "panda-base": {
    effort: 2,
    access: "walk",
    realityEn:
      "Pandas are most active in the morning feeding window; by afternoon they are usually asleep and largely out of sight.",
    realityCn: "熊猫上午进食时段最活跃；下午基本在睡觉，很可能什么都看不到。",
    tipsEn: ["Arrive at opening — ideally before 9am", "The site is large; there is a shuttle"],
    tipsCn: ["开园就到，最好九点前", "园区很大，有观光车"],
  },
  "sichuan-hotpot": {
    effort: 1,
    access: "flat",
    tipsEn: [
      "Ask for 鸳鸯锅 (split pot) if you are unsure about the heat",
      "The oil dish is not optional — it cools the spice",
    ],
    tipsCn: ["不确定辣度就点鸳鸯锅", "油碟不是可选项，是用来解辣的"],
  },
  "peoples-park": {
    effort: 1,
    access: "flat",
    tipsEn: ["Ear cleaning and tea are the point; go on a weekday for a seat"],
    tipsCn: ["喝茶掏耳朵是精髓；工作日才有位子"],
  },
  "sichuan-opera": {
    effort: 1,
    access: "flat",
    realityEn:
      "Tourist-facing shows are short and heavily abridged; face-changing is only a few minutes of the programme.",
    realityCn: "面向游客的演出短且删减多，变脸只占其中几分钟。",
  },
  "jiaozi-towers": {
    effort: 1,
    access: "flat",
    tipsEn: ["Light show runs on the hour; watch from the lawn opposite"],
    tipsCn: ["灯光秀整点开始，在对面草坪看最好"],
  },
  "jinsha-museum": { effort: 2, access: "flat" },

  // ---------------------------------------------------------------- 乐山
  "leshan-buddha": {
    effort: 4,
    access: "stairs",
    realityEn:
      "To see the Buddha full-height you descend a narrow, single-file staircase carved into the cliff, then climb all the way back up. Queues of 1-2 hours are normal. The boat option skips the stairs entirely but only gives you the river view.",
    realityCn:
      "要看全身必须走凌云栈道 —— 崖壁上单行的窄陡台阶，下去再原路爬回来，排队一两小时是常态。坐船可以完全避开台阶，但只能远观。",
    tipsEn: ["Take the boat if stairs or crowds are a problem", "Not advisable with a stroller"],
    tipsCn: ["体力或人多受不了就坐船", "推婴儿车基本无法通行"],
  },

  // ---------------------------------------------------------------- 重庆
  hongyadong: {
    effort: 2,
    access: "stairs",
    realityEn:
      "The famous night facade is best seen from across the river or the bridge — inside it is a crowded shopping complex.",
    realityCn: "著名夜景要从对岸或桥上看；里面其实是拥挤的商场。",
    seasonEn: "Extremely crowded on weekends and holidays.",
    seasonCn: "周末和节假日极度拥挤。",
  },
  "liziba-8d": {
    effort: 2,
    access: "train",
    realityEn:
      "The train-through-building moment lasts seconds; the viewing platform is small and queued.",
    realityCn: "轻轨穿楼就那么几秒，观景platform小且要排队。",
  },
  "shancheng-lane": {
    effort: 3,
    access: "stairs",
    realityEn:
      "Chongqing is built on hills — expect long stair sequences almost everywhere, including between metro exits.",
    realityCn: "重庆是山城，到处是长段台阶，连地铁出口之间都可能爬很久。",
    tipsEn: ["Wear real shoes; map distances are misleading in 3D"],
    tipsCn: ["穿好走的鞋；地图上的直线距离在立体城市里没有参考意义"],
  },

  // ---------------------------------------------------------------- 上海
  "the-bund": {
    effort: 2,
    access: "flat",
    seasonEn:
      "The riverside promenade is packed every evening and on holidays becomes one-way controlled.",
    seasonCn: "外滩每晚人都很多，节假日会单向限流。",
  },
  "yu-garden": {
    effort: 2,
    access: "walk",
    realityEn:
      "The garden itself is ticketed and quiet; the famous bazaar outside is free, crowded and commercial.",
    realityCn: "豫园本体要门票、相对安静；外面著名的城隍庙商圈免费但拥挤商业。",
  },
  "french-concession": { effort: 2, access: "walk" },
  zhujiajiao: {
    effort: 2,
    access: "boat",
    realityEn:
      "A restored water town an hour out of the city; charming early, very crowded by midday.",
    realityCn: "市区一小时外的水乡，早上安静，中午后非常挤。",
  },
  "lujiazui-skydeck": {
    effort: 1,
    access: "cable",
    tipsEn: ["Haze is common — check visibility before buying a ticket"],
    tipsCn: ["雾霾常见，买票前先看能见度"],
  },
  "huangpu-cruise": { effort: 1, access: "boat" },
  "shanghai-breakfast": {
    effort: 2,
    access: "walk",
    tipsEn: ["Most stalls are done by 9am"],
    tipsCn: ["多数摊位九点就收了"],
  },
  "west-bund-art": { effort: 2, access: "walk" },
  "shanghai-disneyland": {
    effort: 4,
    access: "walk",
    realityEn:
      "A full day on your feet with long queues; the paid fast pass changes the experience substantially.",
    realityCn: "全天步行加长时间排队；是否买快速通行证体验差别很大。",
    seasonEn: "Avoid national holidays entirely.",
    seasonCn: "国家法定假日请直接避开。",
  },
  "shanghai-bars": { effort: 1, access: "flat" },
  "shanghai-museum": {
    effort: 2,
    access: "flat",
    tipsEn: ["Free entry but advance booking is required"],
    tipsCn: ["免费但需提前预约"],
  },

  // ---------------------------------------------------------------- 杭州
  westlake: {
    effort: 3,
    access: "walk",
    realityEn: "The full lake loop is about 15km — pleasant by bike, a long day on foot.",
    realityCn: "环湖一圈约 15 公里，骑车轻松，走路是一整天。",
    seasonEn: "Peak lotus season and holidays bring enormous crowds.",
    seasonCn: "荷花季和节假日人非常多。",
  },
  lingyin: {
    effort: 2,
    access: "walk",
    tipsEn: ["The grottoes outside the temple need a separate ticket from the temple itself"],
    tipsCn: ["飞来峰造像和寺内门票分开，需买两次"],
  },
  "longjing-tea": {
    effort: 2,
    access: "walk",
    seasonEn: "Picking season is late March to April; outside it the terraces are quiet but bare.",
    seasonCn: "采茶季在三月底到四月；其他时间茶园安静但没什么可看。",
  },
  "xixi-wetland": { effort: 1, access: "boat" },
  liangzhu: {
    effort: 2,
    access: "walk",
    realityEn:
      "An archaeological site — mostly earthworks and reconstruction rather than standing ruins.",
    realityCn: "考古遗址，主要是土台和复原展示，不是地面古建。",
  },

  // ---------------------------------------------------------------- 苏州
  "suzhou-gardens": {
    effort: 2,
    access: "walk",
    realityEn:
      "The famous gardens are small and become very crowded; going at opening makes a large difference.",
    realityCn: "名园面积不大，人一多体验骤降；开园即到差别很大。",
    tipsEn: ["Humble Administrator's Garden requires booking in peak season"],
    tipsCn: ["拙政园旺季需预约"],
  },
  "pingjiang-road": { effort: 2, access: "walk" },
  "suzhou-museum": {
    effort: 2,
    access: "flat",
    tipsEn: ["Free, but timed booking opens days ahead and goes fast"],
    tipsCn: ["免费，但分时预约提前几天放票，很快约满"],
  },
  "kunqu-pingtan": {
    effort: 1,
    access: "flat",
    realityEn:
      "Performances are in dialect with limited translation — it is atmosphere more than narrative.",
    realityCn: "用方言演唱，翻译有限 —— 听的是氛围不是剧情。",
  },

  // ---------------------------------------------------------------- 南京
  "zijin-mountain": {
    effort: 4,
    access: "stairs",
    realityEn:
      "The Sun Yat-sen Mausoleum is a 392-step climb, and the mountain's sites are far apart — it is a full day with real walking.",
    realityCn: "中山陵是 392 级台阶，且钟山各景点相距很远 —— 是需要认真走路的一整天。",
    tipsEn: ["There is a shuttle between the main sites"],
    tipsCn: ["各景点之间有观光车"],
  },
  "qinhuai-river": { effort: 1, access: "boat" },
  "nanjing-museum": { effort: 2, access: "flat" },
  "jiming-wall": {
    effort: 2,
    access: "stairs",
    seasonEn:
      "Cherry blossom season in late March makes this one of the most crowded spots in the city.",
    seasonCn: "三月底樱花季是全城最挤的地方之一。",
  },
  laomendong: { effort: 2, access: "walk" },

  // ---------------------------------------------------------------- 嘉兴
  "wuzhen-night": {
    effort: 2,
    access: "walk",
    realityEn:
      "A managed, ticketed heritage town rather than a living village — beautiful at night, but curated.",
    realityCn: "是收门票、统一运营的景区古镇，不是原生村落 —— 夜景很美，但一切都是设计过的。",
    tipsEn: ["Staying inside the West Scenic Zone gets you the quiet early morning"],
    tipsCn: ["住在西栅景区内才能享受清早的安静"],
  },

  // ---------------------------------------------------------------- 桂林 / 阳朔 / 龙胜
  "li-river": {
    effort: 1,
    access: "boat",
    realityEn:
      "Two very different trips share this name: the big Guilin-Yangshuo cruise (4 hours, enclosed boat) and the short bamboo raft at Xingping. The ¥20-note view is on the raft section.",
    realityCn:
      "同一个名字下有两种完全不同的玩法：桂林到阳朔的大游船（4 小时、封闭船舱），和兴坪的短程竹筏。二十元人民币背景是竹筏那一段。",
    tipsEn: [
      "Rafts are motorised and short — manage expectations",
      "Water level can cancel rafting after heavy rain",
    ],
    tipsCn: ["竹筏是机动的、路程短，别期待太高", "暴雨后水位过高会停运"],
  },
  "yangshuo-biking": {
    effort: 3,
    access: "walk",
    realityEn:
      "The countryside is genuinely lovely, but Yangshuo town itself is a loud tourist strip.",
    realityCn: "田园风光确实好，但阳朔县城本身是很吵的游客街。",
    tipsEn: ["E-bikes make the Yulong River route easy for most fitness levels"],
    tipsCn: ["租电动车走遇龙河路线，体力一般也没问题"],
  },
  xingping: { effort: 2, access: "walk" },
  "longji-terraces": {
    altitude: 880,
    effort: 4,
    access: "stairs",
    realityEn:
      "Villages sit above the road and the viewpoints are reached by long stone stair climbs — cable car covers only part of one area. Terraces are mirrors only when flooded.",
    realityCn: "村寨在公路上方，观景台要爬很长的石阶，索道只覆盖其中一片。梯田只有灌水期才是镜面。",
    tipsEn: ["Porters can carry luggage up; do not bring a wheeled suitcase"],
    tipsCn: ["有挑夫帮忙背行李；千万别带拉杆箱"],
    seasonEn:
      "Flooded mirrors roughly May-June; golden rice around September-October; bare the rest of the year.",
    seasonCn: "灌水期约 5-6 月出镜面；9-10 月金色稻浪；其余时间是空梯田。",
  },

  // ---------------------------------------------------------------- 张家界
  "zhangjiajie-forest": {
    altitude: 1080,
    effort: 2,
    access: "cable",
    realityEn:
      "This is a viewing park, not a hiking destination. The Bailong glass elevator and the cable cars do nearly all the ascending, and the walking between viewpoints is short and paved. If you came to hike, you will be surprised how little walking there is — and how long the queues for the lifts are.",
    realityCn:
      "这里是观景公园，不是徒步目的地。百龙天梯和索道几乎完成了所有爬升，观景台之间是很短的铺装步道。抱着爬山的期待来，会发现几乎没什么可爬 —— 但排队坐电梯和索道的时间很长。",
    tipsEn: [
      "If you do want to walk, Golden Whip Stream is a real 5.7km valley trail on the flat",
      "The park is huge; internal shuttle buses are unavoidable and slow",
      "Tickets are multi-day and fingerprint-linked",
      "Queues for the Bailong elevator can exceed an hour in peak season",
    ],
    tipsCn: [
      "想走路的话，金鞭溪是真正的 5.7 公里溪谷步道，且地势平缓",
      "园区极大，园内环保车必坐且慢",
      "门票是多日票，绑定指纹",
      "旺季百龙天梯排队可能超过一小时",
    ],
    seasonEn: "October holiday week is the worst possible time.",
    seasonCn: "十一黄金周是最糟的时间。",
  },
  "glass-bridge": {
    effort: 2,
    access: "flat",
    minAge: 6,
    realityEn:
      "Timed-entry tickets, shoe covers required, and no stopping for long photo sessions — you keep moving across.",
    realityCn: "分时段票、必须穿鞋套，桥上不允许长时间停留拍照，要一直走。",
    tipsEn: ["Separate from the main Avatar park — a different day"],
    tipsCn: ["和武陵源主景区是分开的，要另安排一天"],
  },
  tianmen: {
    altitude: 1519,
    effort: 4,
    access: "cable",
    realityEn:
      "The 999 steps are only the final stretch to the arch — the cable car covers the mountain itself. You can also reach the arch by escalator, avoiding the steps entirely. The glass skywalk sections are separate paid add-ons.",
    realityCn:
      "999 级台阶只是通往天门洞的最后一段，上山靠索道。还有扶梯可以直达洞口，完全不用爬台阶。玻璃栈道是另外收费的项目。",
    tipsEn: [
      "The cable car is one of the world's longest — 30 minutes each way",
      "Weather closes the summit often",
    ],
    tipsCn: ["索道是世界最长之一，单程约 30 分钟", "山顶经常因天气关闭"],
  },

  // ---------------------------------------------------------------- 丽江 / 大理 / 香格里拉
  "lijiang-oldtown": {
    altitude: 2400,
    effort: 2,
    access: "walk",
    realityEn:
      "At 2,400m some visitors already feel short of breath. The old town is a maintained tourist district with an entry-maintenance fee.",
    realityCn: "海拔 2400 米，部分人已有轻微气短。古城是维护中的旅游区，需交古维费。",
  },
  "jade-dragon": {
    altitude: 4506,
    effort: 3,
    access: "cable",
    minAge: 6,
    realityEn:
      "The cable car lifts you from Lijiang's 2,400m to 4,506m in about 15 minutes, and the boardwalk climbs on to roughly 4,680m. The altitude gain is abrupt and sickness is common even though you barely walk. Oxygen canisters are sold everywhere for a reason.",
    realityCn:
      "索道约 15 分钟就把人从丽江的 2400 米送到 4506 米，栈道还能再走到约 4680 米。海拔骤升，即使几乎不走路也常见高反。到处卖氧气瓶不是没有原因的。",
    tipsEn: [
      "Ascend only after a night or two acclimatising in Lijiang",
      "Not advisable for young children, pregnancy, or heart/lung conditions",
      "The summit boardwalk is still a climb at that altitude",
    ],
    tipsCn: [
      "先在丽江住一两晚适应再上山",
      "幼童、孕妇、心肺疾病者不建议",
      "山顶栈道在那个海拔上依然很费劲",
    ],
  },
  "dali-erhai": {
    altitude: 1970,
    effort: 2,
    access: "drive",
    realityEn: "The lake loop is about 120km — this is a driving or e-bike day, not a walk.",
    realityCn: "环洱海约 120 公里，是开车或骑电动车的一天，不是步行。",
  },
  shangrila: {
    altitude: 3380,
    effort: 2,
    access: "stairs",
    realityEn:
      "Shangri-La sits at 3,380m and many visitors underestimate it — the monastery's main hall is up a long flight of steps that feels much harder than it looks.",
    realityCn:
      "香格里拉海拔 3380 米，很多人低估了它 —— 松赞林寺主殿要爬一长段台阶，实际比看上去累得多。",
    tipsEn: ["Arrive by road from Lijiang rather than flying straight in, if you can"],
    tipsCn: ["条件允许的话从丽江陆路上来，不要直接飞抵"],
  },

  // ---------------------------------------------------------------- 红河 / 西双版纳
  yuanyang: {
    altitude: 1800,
    effort: 2,
    access: "drive",
    realityEn:
      "The terraces are only mirrors when flooded — roughly November to March. Outside that window you are looking at bare earth or green rice, which photographs nothing like the famous images.",
    realityCn:
      "梯田只有灌水期才是镜面，大约十一月到三月。其他时间是裸土或绿禾，和著名照片完全是两回事。",
    tipsEn: [
      "Sunrise and sunset viewpoints are far apart — you need a driver",
      "Fog can erase the view entirely",
    ],
    tipsCn: ["日出和日落观景台相距很远，需要包车", "起雾可能完全看不到"],
    seasonEn: "November-March only for the mirror effect.",
    seasonCn: "只有 11 月至次年 3 月才有镜面效果。",
  },
  xishuangbanna: {
    effort: 2,
    access: "walk",
    seasonEn:
      "Rainy season roughly May-October; Water Splashing Festival in April is extremely busy.",
    seasonCn: "雨季约 5-10 月；四月泼水节期间极其拥挤。",
  },

  // ---------------------------------------------------------------- 敦煌 / 张掖 / 青海
  mogao: {
    effort: 2,
    access: "walk",
    realityEn:
      "Photography is banned inside the caves, visits are guided on a fixed route, and you see only a rotating subset — typically 8 caves. Tickets are strictly capped and sell out.",
    realityCn:
      "洞窟内禁止拍照，全程跟讲解员按固定路线走，只能看到轮换开放的一小部分 —— 通常 8 个窟。门票严格限量，会售罄。",
    tipsEn: ["Book weeks ahead in summer", "Emergency tickets show fewer caves"],
    tipsCn: ["暑期需提前数周预约", "应急票参观的窟更少"],
  },
  "mingsha-camel": {
    effort: 2,
    access: "walk",
    realityEn:
      "Camel rides run on a fixed short circuit in a queue of other camels — it is not a solitary desert crossing.",
    realityCn: "骑骆驼是排队走固定短线路，前后都是别的骆驼 —— 不是孤身穿越沙漠。",
    tipsEn: ["Go near sunset for both light and temperature"],
    tipsCn: ["接近日落时去，光线和温度都最好"],
  },
  "zhangye-danxia": {
    altitude: 1500,
    effort: 2,
    access: "drive",
    realityEn:
      "Colours are dramatically weaker in flat midday light and after rain; many published photos are heavily saturated.",
    realityCn: "正午平光和雨后颜色会弱很多；网上大量照片是重度调色的。",
    tipsEn: ["Late afternoon light is best", "Shuttle between viewing platforms is mandatory"],
    tipsCn: ["下午晚些时候光线最好", "各观景台之间必须坐区间车"],
  },
  "qinghai-lake": {
    altitude: 3260,
    effort: 2,
    access: "drive",
    realityEn:
      "At 3,260m with strong UV and big temperature swings; the lake is vast, so distances between stops are long.",
    realityCn: "海拔 3260 米，紫外线强、温差大；湖极大，各点之间车程很长。",
    seasonEn: "Rapeseed bloom is July-August; winter is severe.",
    seasonCn: "油菜花期在七八月；冬季严寒。",
  },

  // ---------------------------------------------------------------- 新疆
  kanas: {
    altitude: 1370,
    effort: 3,
    access: "walk",
    realityEn:
      "Very remote — long transfers from Urumqi, and the internal shuttle system adds hours.",
    realityCn: "非常偏远 —— 从乌鲁木齐转乘时间很长，园内区间车还要再耗几小时。",
    seasonEn:
      "The famous autumn colour is a narrow window in late September; the area effectively closes in deep winter.",
    seasonCn: "著名的秋色只有九月下旬很短一段；深冬基本封闭。",
  },

  // ---------------------------------------------------------------- 黄山 / 宏村
  huangshan: {
    altitude: 1864,
    effort: 5,
    access: "cable",
    realityEn:
      "Two completely different trips: cable car up and a few paved viewpoints, or the real hike, which is thousands of stone steps and genuinely hard. Sea of clouds is a weather event, not a guarantee — many visitors never see it.",
    realityCn:
      "两种完全不同的玩法：索道上山走几个铺装观景台，或者真正徒步 —— 几千级石阶，是实打实的累。云海是靠天吃饭，不是必现景观，很多人一次也没遇上。",
    tipsEn: [
      "Staying on the summit is expensive and books out, but it is the only way to catch sunrise",
      "Porters carry everything up by hand — pack light",
      "Steps are wet and slick in cloud",
    ],
    tipsCn: [
      "山顶住宿贵且需早订，但看日出只能住山上",
      "所有物资靠挑夫人力上山 —— 轻装",
      "云雾中石阶湿滑",
    ],
    seasonEn: "Winter closes some routes; October holiday is severely overcrowded.",
    seasonCn: "冬季部分路线封闭；十一黄金周严重超载。",
  },
  hongcun: {
    effort: 2,
    access: "walk",
    realityEn:
      "A living village that is also a ticketed film location — busloads arrive mid-morning.",
    realityCn: "既是有人居住的村子也是收门票的取景地 —— 上午十点后旅游大巴陆续到达。",
  },

  // ---------------------------------------------------------------- 九寨沟
  jiuzhaigou: {
    // Most of the boardwalk sits at 2,000-2,500m; only Long Lake, at the top
    // of Zechawa gully, passes 3,000m. Using the summit figure was excluding
    // a valley that is routinely visited with children.
    altitude: 2400,
    altitudeMax: 3100,
    effort: 3,
    access: "drive",
    realityEn:
      "Most of the valley you actually walk is 2,000-2,500m, and you move by internal shuttle between boardwalks. Only Long Lake, at the top of Zechawa gully, climbs past 3,000m. Daily numbers are capped and tickets must be booked ahead.",
    realityCn:
      "实际走的栈道大多在 2000-2500 米，靠观光车在各沟之间移动。只有则查洼沟顶端的长海超过 3000 米。每日限流，必须提前实名预约。",
    tipsEn: [
      "Long Lake is the one stop above 3,000m — skip it if anyone is struggling",
      "Altitude is mild but real — take the upper gullies slowly",
    ],
    tipsCn: [
      "长海是唯一超过 3000 米的点 —— 有人不适就跳过",
      "海拔不算极端但确实存在 —— 上游几条沟慢慢走",
    ],
    seasonEn: "Peak colour is a short October window; parts freeze in winter.",
    seasonCn: "最佳彩林只有十月很短一段；冬季部分封冻。",
  },

  // ---------------------------------------------------------------- 哈尔滨
  "harbin-ice": {
    effort: 2,
    access: "walk",
    minAge: 6,
    realityEn:
      "Temperatures routinely hit -20°C to -30°C and you are outdoors at night. Phone batteries die in minutes and ordinary winter clothing is not enough.",
    realityCn: "常态零下 20 到 30 度，而且是夜间户外活动。手机电量几分钟就掉光，普通冬装完全不够。",
    tipsEn: [
      "Serious boots, base layers and hand warmers are not optional",
      "The festival runs roughly late December to February only",
    ],
    tipsCn: ["专业保暖鞋、秋衣秋裤、暖宝宝是必需品", "冰雪大世界大致只在十二月底到二月开放"],
    seasonEn: "Winter only.",
    seasonCn: "仅冬季。",
  },

  // ---------------------------------------------------------------- 三亚 / 厦门 / 福建
  "yalong-bay": {
    effort: 1,
    access: "flat",
    seasonEn:
      "Typhoon season is roughly August-October; winter is peak domestic travel and prices spike.",
    seasonCn: "台风季约 8-10 月；冬季是国内旅游旺季，价格飙升。",
  },
  wuzhizhou: {
    effort: 2,
    access: "boat",
    minAge: 8,
    realityEn:
      "Snorkelling here means open water from a boat, not a shallow lagoon — children need to be confident swimmers, and rough sea cancels trips at short notice.",
    realityCn:
      "这里的浮潜是从船上下开放海域，不是浅水泻湖 —— 孩子需要能自如游泳，且海况不好会临时取消。",
    tipsEn: ["Operators vary widely in safety standards — check equipment and guide ratios"],
    tipsCn: ["各运营商安全标准差异很大 —— 注意装备状况和教练配比"],
  },
  gulangyu: {
    effort: 3,
    access: "walk",
    realityEn:
      "No cars on the island, so everything is on foot and hilly; ferry tickets must be booked ahead and the island is very crowded.",
    realityCn: "岛上禁行机动车，全靠步行且有坡；轮渡票需提前预订，岛上人非常多。",
  },
  "fujian-tulou": {
    effort: 2,
    access: "drive",
    realityEn:
      "The tulou clusters are far apart on mountain roads — a long driving day from Xiamen.",
    realityCn: "各土楼群分散在山路上 —— 从厦门出发是很长的车程一天。",
  },
  "chaoshan-food": {
    effort: 2,
    access: "walk",
    tipsEn: ["Many of the best places are cash-only and close early or open late"],
    tipsCn: ["很多好店只收现金，且营业时间很早关或很晚开"],
  },

  // ---------------------------------------------------------------- 贵州
  "xijiang-miao": {
    altitude: 850,
    effort: 3,
    access: "stairs",
    realityEn:
      "The village climbs a steep hillside — getting to your guesthouse can mean a long stair climb with luggage.",
    realityCn: "寨子建在陡坡上 —— 拖着行李到客栈可能要爬很长的台阶。",
    tipsEn: ["The night view of the lit hillside is the highlight"],
    tipsCn: ["夜晚整个山坡亮灯是精华"],
  },
  huangguoshu: {
    effort: 3,
    access: "stairs",
    realityEn:
      "Several separate scenic areas connected by shuttle, with a lot of steps between viewpoints.",
    realityCn: "由几个分开的景区组成，靠区间车连接，观景台之间台阶很多。",
    seasonEn: "Flow is dramatic in the June-September rainy season and thin in winter.",
    seasonCn: "6-9 月雨季水量壮观，冬季水很小。",
  },

  // ---------------------------------------------------------------- 西藏
  // Every Tibet entry carries altitude because this is the single most
  // common and most serious mismatch between the photos and the trip.
  potala: {
    altitude: 3650,
    effort: 3,
    access: "stairs",
    minAge: 6,
    realityEn:
      "Lhasa sits at 3,650m — altitude sickness is common in the first 48 hours regardless of fitness. Seeing both the White and Red Palaces means roughly 1,000 steps up and down zigzag stone staircases, at altitude, inside a strictly enforced one-hour limit.",
    realityCn:
      "拉萨海拔 3650 米 —— 无论体能如何，抵达头两天高原反应都很常见。走完白宫和红宫，上上下下约 1000 级之字形石阶，而且宫内有严格执行的一小时参观时限。",
    tipsEn: [
      "Spend two nights acclimatising in Lhasa before any sightseeing",
      "Tickets are real-name and capped at about 2,300 a day — book well ahead",
      "Foreign nationals require a Tibet Travel Permit arranged through a tour operator",
    ],
    tipsCn: [
      "先在拉萨适应两晚再开始任何行程",
      "门票实名，每日限额约 2300 张，务必提前预约",
      "外籍人士需通过旅行社办理入藏函",
    ],
  },
  "jokhang-barkhor": {
    altitude: 3650,
    effort: 2,
    access: "walk",
    minAge: 6,
    realityEn:
      "The kora is a living pilgrimage circuit, not a viewpoint — walk it clockwise and photograph people only with care and consent.",
    realityCn: "八廓街转经道是活的宗教场所，不是观景点 —— 顺时针走，拍摄当地人务必谨慎并征得同意。",
    tipsCn: ["寺内部分区域禁止拍照"],
    tipsEn: ["Photography is banned in parts of the temple interior"],
  },
  yamdrok: {
    // The lake surface is 4,441m, but the viewpoint everyone is driven to is
    // the Kamba La pass at roughly 4,770m. Use the height people stand at.
    altitude: 4770,
    effort: 1,
    access: "drive",
    minAge: 8,
    realityEn:
      "The lake is 4,441m, but you view it from the Kamba La pass at about 4,770m — over a kilometre higher than Lhasa. You barely walk, so effort tells you nothing; some people feel it the moment they step out of the car.",
    realityCn:
      "湖面海拔 4441 米，但观景点在岗巴拉山口，约 4770 米 —— 比拉萨高出一千多米。几乎不用走路，所以体力强度完全不能说明问题；有些人一下车就有明显反应。",
    tipsEn: [
      "A long day trip from Lhasa on winding roads — motion sickness is common",
      "Photographing the decorated yaks is a paid transaction",
    ],
    tipsCn: ["从拉萨出发是盘山路的长途一日游，晕车常见", "和装饰过的牦牛合影是要收费的"],
  },
  "everest-bc": {
    altitude: 5200,
    effort: 3,
    access: "drive",
    minAge: 14,
    realityEn:
      "The north base camp is at about 5,200m. This is genuine high altitude where serious altitude illness is a real risk, accommodation is basic and unheated, and there is no quick medical help. Reaching it requires permits and a guided vehicle — it is not a place to improvise.",
    realityCn:
      "珠峰北坡大本营海拔约 5200 米。这是真正的高海拔，严重高原病是实际风险，住宿简陋无供暖，且没有快速医疗支援。需办理边防证并由持证车辆带入 —— 不是可以临时起意的地方。",
    tipsEn: [
      "Requires a week of prior acclimatisation in Tibet — do not go early in a trip",
      "Not suitable for children, pregnancy, or anyone with heart or lung conditions",
      "The mountain is frequently hidden by cloud even in good season",
    ],
    tipsCn: [
      "需要在西藏适应约一周后再前往，不要安排在行程前段",
      "儿童、孕妇、心肺疾病者不适合",
      "即使在好季节，珠峰也经常被云遮住",
    ],
  },
  nyingchi: {
    // Bayi town, where visitors stay, is 2,994m — Nyingchi is the lowest
    // prefecture in Tibet and the gentlest introduction to the plateau.
    altitude: 2994,
    altitudeMax: 4000,
    effort: 2,
    access: "drive",
    realityEn:
      "Lower and greener than Lhasa, but the peach blossom is a two-to-three week window in late March and early April.",
    realityCn:
      "比西藏其他地方低得多也绿得多 —— 八一镇约 2994 米，拉萨是 3650 米 —— 是上高原最温和的入口。桃花期只有三月底到四月初的两三周。",
    seasonEn: "Peach blossom: late March to early April only.",
    seasonCn: "桃花期：仅三月底至四月初。",
  },

  // ------------------------------------------------- 上海（第二批）
  tianzifang: {
    effort: 2,
    access: "walk",
    realityEn:
      "The lanes are barely two people wide and it is one of the densest crowds in the city at weekends. Go on a weekday morning or not at all.",
    realityCn: "弄堂宽度勉强两人并行，周末是全市最挤的地方之一。要么工作日上午去，要么别去。",
    tipsEn: ["People still live upstairs — keep the noise down and do not photograph into windows"],
    tipsCn: ["楼上仍有居民 —— 请压低音量，不要对着窗户拍照"],
  },
  "m50-art": {
    effort: 2,
    access: "walk",
    realityEn:
      "Individual galleries keep their own hours and many close Mondays; some spaces are empty between shows.",
    realityCn: "各画廊自定开放时间，不少周一闭馆；换展期间有些空间是空的。",
  },
  "power-station-art": {
    effort: 2,
    access: "flat",
    realityEn:
      "The building is the main event — exhibitions rotate and can be sparse. Check what is actually on before crossing town.",
    realityCn: "建筑本身才是主角 —— 展览轮换，有时内容很少。过江之前先查当期展。",
  },
  "jingan-temple": {
    effort: 1,
    access: "flat",
    realityEn:
      "Small, and it is a working temple in the middle of a shopping district — twenty minutes, not an afternoon.",
    realityCn: "面积小，且是商圈中间的活跃寺庙 —— 二十分钟的事，不是一个下午。",
  },
  "longhua-temple": {
    effort: 2,
    access: "walk",
    seasonEn: "The peach blossoms in late March are the one time it gets busy.",
    seasonCn: "三月底桃花开时是唯一人多的时候。",
  },
  qibao: {
    effort: 2,
    access: "walk",
    realityEn:
      "Much smaller than Zhujiajiao — an hour covers it. The draw is that it is on the metro, not that it is unspoilt.",
    realityCn: "比朱家角小得多，一小时能走完。优点是地铁可达，不是原生态。",
  },
  "shanghai-natural-history": {
    effort: 2,
    access: "flat",
    tipsEn: [
      "Book ahead; weekend slots go early",
      "Allow two hours — the spiral layout is one way",
    ],
    tipsCn: ["需提前预约，周末场次很快约满", "预留两小时，螺旋动线是单向的"],
  },
  "shanghai-acrobatics": {
    effort: 1,
    access: "flat",
    realityEn:
      "About 90 minutes with no interval, and seats are fixed — book properly rather than at the door.",
    realityCn: "约 90 分钟无中场，对号入座 —— 提前订票，不要现场买。",
  },
  "nanjing-road": {
    effort: 2,
    access: "flat",
    realityEn:
      "A shopping street first and a sight second. The stretch toward the Bund is the part worth walking.",
    realityCn: "首先是商业街，其次才是景点。靠外滩那一段才值得走。",
    tipsEn: ["Ignore anyone approaching you about a tea ceremony or an art student exhibition"],
    tipsCn: ["有人搭讪邀请喝茶或看画展，一律不要理会"],
  },
  "xuhui-riverside": {
    effort: 2,
    access: "walk",
    realityEn:
      "Wide open and almost entirely unshaded — pleasant in spring and autumn, punishing in July.",
    realityCn: "开阔且几乎没有遮阴 —— 春秋很舒服，七月很难受。",
    seasonEn: "Best March-June and September-November.",
    seasonCn: "最佳时间 3-6 月和 9-11 月。",
  },

  // ------------------------------------------------- 北京（第二批）
  "summer-palace": {
    effort: 3,
    access: "walk",
    realityEn:
      "Roughly three square kilometres, most of it around a lake — a full half day of walking, and the Long Corridor alone is 700m. There is a boat if legs are the limit.",
    realityCn:
      "占地约三平方公里，大部分绕着昆明湖 —— 是实打实走半天，光长廊就有 700 米。走不动可以坐船。",
    tipsEn: ["Enter at the North Palace Gate to start at the top of the hill and walk down"],
    tipsCn: ["从北宫门进可以先上山再一路往下走"],
    seasonEn: "The lake freezes in winter and boats stop.",
    seasonCn: "冬季湖面结冰，船停运。",
  },
  "lama-temple": {
    effort: 2,
    access: "walk",
    realityEn:
      "An active place of worship, not a museum — incense is handed out free at the gate and the smoke is heavy.",
    realityCn: "是活跃的宗教场所不是博物馆 —— 门口免费发香，烟很大。",
    tipsEn: ["Photography is restricted inside the halls"],
    tipsCn: ["殿内限制拍照"],
  },
  "beihai-park": {
    effort: 2,
    access: "walk",
    seasonEn:
      "Boating runs roughly April to October; the lake is used for skating when it freezes.",
    seasonCn: "划船大约 4-10 月；结冰后可以滑冰。",
  },
  houhai: {
    effort: 2,
    access: "walk",
    realityEn:
      "The lakeside bar strip is aggressively touristy and touts are constant. One alley back is where locals actually are.",
    realityCn: "湖边酒吧街游客化严重，拉客不断。往里走一条胡同才是本地人待的地方。",
  },
  nanluoguxiang: {
    effort: 2,
    access: "walk",
    realityEn:
      "The main lane is a souvenir corridor. The hutongs running east and west off it are the actual thing you came for.",
    realityCn: "主街基本是纪念品走廊。东西两侧岔出去的胡同才是真正想看的东西。",
  },
  "jiankou-wall": {
    effort: 5,
    access: "walk",
    minAge: 16,
    realityEn:
      "This is unrestored wall: no handrails, no steps in places, loose brick, and sheer drops on both sides of a ridge. It is not an official scenic area, there are no staff, and people are injured and occasionally killed here. Treat it as mountaineering, not sightseeing.",
    realityCn:
      "这是未修复的野长城：没有护栏，部分路段没有台阶，砖块松动，山脊两侧都是断崖。它不是正式景区，没有工作人员，每年都有人在此受伤甚至死亡。请按登山对待，不是观光。",
    tipsEn: [
      "Go with someone who has done it before, or with a guide — not off a map",
      "Gloves, real boots, and far more water than you think",
      "Turn back in rain or wind; the brick is lethal when wet",
    ],
    tipsCn: [
      "跟去过的人或向导一起，不要照着地图自己去",
      "手套、正经登山鞋，以及远超预估的水",
      "下雨或大风就折返；砖面湿了极其危险",
    ],
    seasonEn: "Spring and autumn only; summer heat and winter ice both make it worse.",
    seasonCn: "只在春秋去；夏季高温和冬季结冰都会让它更危险。",
  },
  panjiayuan: {
    effort: 2,
    access: "flat",
    realityEn:
      "Most of it is reproduction, and that is fine if you know it. Busiest and best at weekends; many stalls do not bother midweek.",
    realityCn: "大部分是仿制品，心里有数就无所谓。周末最热闹也最值得去，平日很多摊位根本不出摊。",
    tipsEn: ["Bargain hard — opening prices assume it", "Do not buy anything as a genuine antique"],
    tipsCn: ["认真讲价 —— 开价就是等着还的", "不要当成真古董买"],
  },
  "beijing-duck": {
    effort: 1,
    access: "flat",
    realityEn:
      "The famous chains are the touristy ones. A duck takes 40-60 minutes to roast, so book or order it when you sit down.",
    realityCn: "最有名的连锁店恰恰是游客店。烤一只鸭要 40-60 分钟，务必提前订或一坐下就点。",
  },
  "olympic-park": {
    effort: 3,
    access: "walk",
    realityEn:
      "Enormous and largely empty concrete — impressive at night when lit, a long exposed walk by day. Going inside the Bird's Nest is a separate ticket.",
    realityCn:
      "极大且空旷的混凝土广场 —— 夜晚亮灯很壮观，白天是漫长的暴晒步行。进鸟巢内部要另买票。",
  },
  "universal-beijing": {
    effort: 4,
    access: "walk",
    realityEn:
      "A full day on your feet with queues measured in hours. Tickets are date-specific and sell out; the express pass changes the day substantially.",
    realityCn: "全天步行，排队以小时计。门票分日期且会售罄；买不买快速通行证体验差别很大。",
    tipsEn: [
      "Book weeks ahead for holidays",
      "It is an hour from the centre — this is a whole day, not an afternoon",
    ],
    tipsCn: ["节假日需提前几周订票", "距市中心一小时车程 —— 这是一整天，不是一个下午"],
    seasonEn: "Brutal in July and August heat, and in deep winter cold.",
    seasonCn: "七八月酷热、隆冬严寒时都很难熬。",
  },
  yuanmingyuan: {
    effort: 3,
    access: "walk",
    realityEn:
      "Mostly parkland; the famous carved ruins are one small corner at the far end, and it is a long walk to reach them.",
    realityCn: "大部分是园林；著名的西洋楼残迹只在最远端一小块，走过去很远。",
    tipsEn: ["There is an internal shuttle — worth it"],
    tipsCn: ["园内有电瓶车，值得坐"],
  },
  "capital-museum": {
    effort: 2,
    access: "flat",
    tipsEn: ["Free with passport booking; far quieter than the National Museum"],
    tipsCn: ["凭护照免费预约；比国家博物馆安静得多"],
  },
  "peking-opera": {
    effort: 1,
    access: "flat",
    realityEn:
      "Sung in stylised Mandarin that Chinese audiences also read surtitles for. Tourist theatres run shortened programmes for exactly this reason — an hour is the right dose.",
    realityCn:
      "用程式化的唱腔演唱，中文观众也要看字幕。游客剧场演删减版正是因为这个 —— 一小时刚好。",
  },

  // ---------------------------------------------------------------- 深圳
  // Authored from scratch — Shenzhen had no catalog entries before. Treat the
  // taste scores as editorial; the practical notes below are the checkable part.
  "shenzhen-bay": {
    effort: 2,
    access: "walk",
    realityEn:
      "The path is flat and continuous, but it is 15km end to end — most people rent a bike rather than walk a fraction of it. The Hong Kong skyline across the bay is the view, not the park itself.",
    realityCn:
      "步道平坦连续，但全长 15 公里 —— 多数人租车骑，走路只能走一小段。真正的景是对岸的香港天际线，不是公园本身。",
    tipsEn: [
      "Sunset is the reason to come",
      "Bike docks are along the promenade; bring a local payment app",
    ],
    tipsCn: ["日落是来这里的理由", "沿途都有共享单车，需要本地支付方式"],
  },
  huaqiangbei: {
    effort: 2,
    access: "flat",
    realityEn:
      "It is a working wholesale market, not an attraction. Vast, fluorescent-lit and aimed at trade buyers — fascinating if you like how things are made, tedious if you do not.",
    realityCn:
      "这是运作中的批发市场，不是景点。巨大、日光灯、面向业内买家 —— 对硬件感兴趣的人会着迷，否则会很无聊。",
    tipsEn: [
      "Retail prices are not automatically cheap; bargaining is expected",
      "Weekday mornings are when it is actually alive",
    ],
    tipsCn: ["零售价不一定便宜，讲价是常态", "工作日上午才是真正热闹的时候"],
  },
  "shekou-seaworld": {
    effort: 2,
    access: "walk",
    seasonEn: "Fountain show runs evenings; check the schedule, it changes seasonally.",
    seasonCn: "音乐喷泉在晚间，场次随季节变动，去前先查。",
  },
  dameisha: {
    effort: 1,
    access: "flat",
    realityEn:
      "Free and genuinely public, which is also the problem — on a summer weekend it is shoulder to shoulder and the water is not clear.",
    realityCn: "免费且真正对公众开放，问题也在于此 —— 夏季周末人挤人，水也不清澈。",
    tipsEn: [
      "Reservation may be required in peak summer",
      "Xiaomeisha next door is ticketed and quieter",
    ],
    tipsCn: ["盛夏可能需要预约", "隔壁小梅沙收费，人少一些"],
    seasonEn: "Swimming season is roughly April to October; typhoons close it outright.",
    seasonCn: "游泳季约 4-10 月；台风天直接关闭。",
  },
  dongxichong: {
    effort: 5,
    access: "walk",
    minAge: 14,
    realityEn:
      "This is a genuine coastal scramble, not a beach walk: roughly 8km over wet boulders with no shade, no exit points in the middle, and sections that are impassable at high tide. People are rescued from it every year.",
    realityCn:
      "这是真正的海岸穿越，不是海滨散步：约 8 公里湿滑礁石，全程无遮阴，中段无撤离点，涨潮时部分路段无法通过。每年都有人在这里被救援。",
    tipsEn: [
      "Check the tide table before setting out — this is the one that gets people hurt",
      "Allow 6-8 hours, carry all your water, and do not start in the afternoon",
      "Not a solo trip, and not one to do in summer heat",
    ],
    tipsCn: [
      "出发前务必查潮汐表 —— 出事的就是这一条",
      "预留 6-8 小时，自带全部饮水，不要下午才出发",
      "不要独自前往，也不要在盛夏高温时去",
    ],
    seasonEn: "Best in spring and late autumn; closed or dangerous around typhoons.",
    seasonCn: "春季和深秋最好；台风前后危险或封闭。",
  },
  "oct-loft": {
    effort: 2,
    access: "walk",
    realityEn: "Small — an afternoon at most, and many galleries close Mondays.",
    realityCn: "范围不大，最多待一个下午，且不少展馆周一闭馆。",
  },
  lianhuashan: {
    effort: 2,
    access: "stairs",
    realityEn:
      "The skyline view needs a 20-30 minute uphill walk; there is no cable car. Haze frequently flattens the view entirely.",
    realityCn: "看天际线要走 20-30 分钟上坡，没有索道。雾霾天视野可能完全没有。",
    tipsEn: ["Check air quality the same day — this is the deciding factor"],
    tipsCn: ["当天先看空气质量，这是成败关键"],
  },
  "dapeng-fortress": {
    effort: 2,
    access: "walk",
    realityEn:
      "Far out on the eastern peninsula — over an hour each way from the city centre, so pair it with the coast rather than treating it as a quick stop.",
    realityCn:
      "在东部半岛深处，从市区单程一个多小时 —— 建议和东部海岸线一起安排，不要当成顺路的小景点。",
  },
  "window-of-world": {
    effort: 3,
    access: "walk",
    realityEn:
      "Replica landmarks at reduced scale; it is a theme park, not a sight. Judge it as one.",
    realityCn: "缩比复制的世界地标 —— 它是主题公园不是景点，请按主题公园的标准判断。",
    seasonEn: "Brutal in midsummer heat with little shade.",
    seasonCn: "盛夏酷热且缺少遮阴。",
  },
  "shenzhen-food": {
    effort: 2,
    access: "walk",
    tipsEn: ["Chaoshan beef hotpot is the local speciality worth planning around"],
    tipsCn: ["潮汕牛肉火锅是本地最值得专程去吃的"],
  },

  // ---------------------------------------------------------------- 香港
  "victoria-peak": {
    effort: 2,
    access: "cable",
    realityEn:
      "The Peak Tram queue can exceed an hour; the free public viewpoints are nearly as good as the paid Sky Terrace.",
    realityCn: "山顶缆车排队可能超过一小时；免费的公共观景点其实和付费的凌霄阁差不多。",
    tipsEn: ["Bus or taxi up avoids the tram queue entirely"],
    tipsCn: ["坐巴士或打车上山可以完全避开缆车排队"],
  },
  "star-ferry": {
    effort: 1,
    access: "boat",
    tipsEn: ["One of the best-value views anywhere — a few HKD"],
    tipsCn: ["几块港币，性价比最高的景观之一"],
  },
  "hk-food": {
    effort: 2,
    access: "walk",
    tipsEn: ["Shared tables are normal; service is brisk, not rude"],
    tipsCn: ["拼桌是常态；服务快而直接，不是没礼貌"],
  },
  "lantau-buddha": {
    effort: 3,
    access: "cable",
    realityEn:
      "268 steps to the Buddha after the cable car, and the cable car itself closes for maintenance periods and in high wind.",
    realityCn: "坐完缆车还要爬 268 级台阶到大佛；缆车本身有检修期，大风天也会停运。",
    tipsEn: ["Check the cable car is running before travelling out"],
    tipsCn: ["出发前先确认缆车是否运行"],
  },
  "dragons-back": {
    effort: 4,
    access: "walk",
    realityEn:
      "A real coastal ridge hike of 2-3 hours with sun exposure and no shade for long stretches.",
    realityCn: "真正的海岸山脊徒步，2-3 小时，长段暴晒无遮阴。",
    tipsEn: ["Carry water; summer humidity makes this much harder than the distance suggests"],
    tipsCn: ["自带水；夏季湿热会让这条路比距离显示的难得多"],
  },
  "lan-kwai-fong": { effort: 1, access: "flat" },
  mplus: { effort: 2, access: "flat" },

  // ---------------------------------------------------------------- 台湾
  "taipei-101": {
    effort: 1,
    access: "cable",
    tipsEn: ["Haze and cloud frequently spoil the view — check before buying"],
    tipsCn: ["雾霾和云层经常影响视野，买票前先确认"],
  },
  "night-markets-tw": {
    effort: 2,
    access: "walk",
    tipsEn: ["Cash is still king at most stalls"],
    tipsCn: ["多数摊位仍以现金为主"],
  },
  "palace-museum-tp": {
    effort: 2,
    access: "flat",
    realityEn:
      "The collection rotates — specific famous pieces may not be on display when you visit.",
    realityCn: "藏品是轮展的 —— 你想看的那件名品当天不一定在展。",
  },
  jiufen: {
    effort: 3,
    access: "stairs",
    realityEn:
      "The photogenic teahouse lanes are narrow stair alleys that become almost impassable on weekend afternoons.",
    realityCn: "出片的茶楼老街是狭窄的阶梯巷道，周末下午几乎寸步难行。",
    tipsEn: ["Go on a weekday, or after the day-trip buses leave in the evening"],
    tipsCn: ["工作日去，或等傍晚旅游大巴离开后"],
  },
  alishan: {
    altitude: 2216,
    effort: 3,
    access: "train",
    realityEn:
      "The sunrise train leaves in the dark and is often fully booked; the 'sea of clouds' depends entirely on weather and is missed as often as it is seen.",
    realityCn: "看日出的小火车天没亮就发车，且经常订满；云海完全看天，看不到的次数和看到的一样多。",
    tipsEn: [
      "Stay on the mountain the night before — day trips cannot make the sunrise",
      "It is cold at dawn even in summer",
    ],
    tipsCn: ["前一晚必须住山上，当天往返赶不上日出", "即使夏天，清晨也很冷"],
  },
  "sun-moon-lake": { altitude: 748, effort: 2, access: "boat" },
  kenting: {
    effort: 1,
    access: "flat",
    seasonEn: "Typhoon season roughly July-September.",
    seasonCn: "台风季约 7-9 月。",
  },

  // ---------------------------------------------------------------- 晋中 / 洛阳 / 大同 / 澳门
  pingyao: {
    effort: 3,
    access: "walk",
    realityEn:
      "A walled city where you walk everywhere on stone streets; the through-ticket covers many small sites you may not want.",
    realityCn: "城内全靠步行走石板路；通票包含很多你未必想看的小景点。",
  },
  longmen: {
    effort: 3,
    access: "stairs",
    realityEn:
      "The grottoes stretch along both riverbanks with many stairs; the largest Buddha is at the top of a long climb.",
    realityCn: "石窟沿伊河两岸展开，台阶很多；最大的卢舍那大佛在一段长爬升的顶端。",
    tipsEn: ["Little shade — hard in summer heat"],
    tipsCn: ["几乎没有遮阴，盛夏很难熬"],
  },
  yungang: {
    effort: 3,
    access: "walk",
    realityEn:
      "Yungang is level and easy, but the Hanging Temple is a separate site an hour away with narrow, exposed walkways and a strict visitor cap.",
    realityCn:
      "云岗石窟地势平缓好走，但悬空寺是一小时车程外的另一个景点，栈道窄且临空，还有严格限流。",
    tipsEn: ["The Hanging Temple is not advisable if you fear heights"],
    tipsCn: ["恐高者不建议登悬空寺"],
  },
  "macau-food-street": {
    effort: 2,
    access: "stairs",
    realityEn: "The ruins and surrounding lanes involve steady uphill stair climbs.",
    realityCn: "大三巴及周边小巷是持续的上坡台阶。",
    seasonEn: "Weekends bring heavy cross-border crowds.",
    seasonCn: "周末大量跨境游客，人非常多。",
  },
};

/** A compact badge for the swipe card. Kept to at most three. */
export interface GuideChip {
  icon: string;
  label: string;
  /** true when this is a warning rather than neutral information. */
  warn: boolean;
}

const ACCESS_LABEL: Record<string, { en: string; cn: string; icon: string }> = {
  cable: { en: "cable car", cn: "索道为主", icon: "🚡" },
  stairs: { en: "stairs", cn: "台阶多", icon: "⛰️" },
  walk: { en: "on foot", cn: "步行", icon: "🚶" },
  boat: { en: "by boat", cn: "坐船", icon: "⛵" },
  drive: { en: "by car", cn: "车程长", icon: "🚗" },
  train: { en: "by train", cn: "小火车", icon: "🚂" },
  flat: { en: "level", cn: "平地", icon: "▫️" },
};

/**
 * The two or three things worth knowing before you swipe right.
 *
 * Deliberately terse — the full guide lives on the itinerary screen. Altitude
 * leads whenever it is high enough to matter, because that is the failure
 * mode people most often walk into.
 */
export function guideChips(g: Guide | undefined, lang: "en" | "cn" = "en"): GuideChip[] {
  if (!g) return [];
  const out: GuideChip[] = [];
  if (g.altitude && g.altitude >= 2500) {
    out.push({ icon: "🫁", label: `${g.altitude}m`, warn: g.altitude >= 3000 });
  }
  const a = ACCESS_LABEL[g.access];
  if (a && g.access !== "flat")
    out.push({ icon: a.icon, label: lang === "cn" ? a.cn : a.en, warn: false });
  if (g.effort >= 4) {
    out.push({
      icon: "🥾",
      label:
        lang === "cn"
          ? g.effort === 5
            ? "体力硬核"
            : "体力较大"
          : g.effort === 5
            ? "strenuous"
            : "tiring",
      warn: g.effort === 5,
    });
  }
  if (g.minAge && out.length < 3) {
    out.push({ icon: "👶", label: lang === "cn" ? `${g.minAge}岁+` : `${g.minAge}+`, warn: true });
  }
  return out.slice(0, 3);
}

/** Does this entry have anything worth expanding on the itinerary screen? */
export const hasFullGuide = (g: Guide | undefined): boolean =>
  Boolean(g && (g.realityEn || g.tipsEn?.length || g.seasonEn));
